import type { RadioStation, AudioSource, AudioSourceType, TranscribeRequest, TranscribeResponse } from './types.ts';

// ─── Radio Browser API ────────────────────────────────────────────────────────
// https://api.radio-browser.info — free, CORS-enabled, community-maintained

const RADIO_BROWSER_HOSTS = [
  'https://de1.api.radio-browser.info',
  'https://nl1.api.radio-browser.info',
  'https://at1.api.radio-browser.info',
];

function radioBrowserBase(): string {
  return RADIO_BROWSER_HOSTS[Math.floor(Math.random() * RADIO_BROWSER_HOSTS.length)];
}

async function radioBrowserFetch<T>(path: string): Promise<T> {
  const base = radioBrowserBase();
  const res = await fetch(`${base}/json${path}`, {
    headers: { 'User-Agent': 'VoxAether/1.0 (https://github.com/vg-robin/VoxAether)' },
  });
  if (!res.ok) throw new Error(`Radio Browser API error: ${res.status}`);
  return res.json() as Promise<T>;
}

/** Convert a RadioStation API record to our internal AudioSource shape */
function stationToSource(s: RadioStation): AudioSource | null {
  const lat = s.geo_lat;
  const lng = s.geo_long;
  if (lat == null || lng == null || lat === 0 || lng === 0) return null;
  return {
    id: s.stationuuid,
    type: 'radio',
    title: s.name.trim() || 'Unknown Station',
    country: s.country || '',
    countrycode: (s.countrycode || '').toUpperCase(),
    language: s.language || '',
    lat,
    lng,
    streamUrl: s.url_resolved || s.url,
    favicon: s.favicon || '',
    tags: s.tags
      ? s.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .slice(0, 5)
      : [],
    bitrate: s.bitrate,
    codec: s.codec,
    votes: s.votes,
  };
}

/**
 * Fetch stations near a lat/lng.  Returns up to `limit` sources with valid geo
 * coordinates, sorted by votes descending.
 */
export async function fetchStationsNear(
  lat: number,
  lng: number,
  limit = 30,
): Promise<AudioSource[]> {
  const raw = await radioBrowserFetch<RadioStation[]>(
    `/stations/bylatlng?lat=${lat}&lng=${lng}&limit=${limit * 3}&order=votes&reverse=true&hidebroken=true`,
  );
  return raw.map(stationToSource).filter((s): s is AudioSource => s !== null).slice(0, limit);
}

/**
 * Fetch a broader set of geolocated stations to populate the initial world map.
 * Uses multiple requests to cover different continents.
 */
export async function fetchWorldStations(limit = 400): Promise<AudioSource[]> {
  const raw = await radioBrowserFetch<RadioStation[]>(
    `/stations?hasGeoInfo=true&limit=${limit}&order=votes&reverse=true&hidebroken=true`,
  );
  const sources = raw.map(stationToSource).filter((s): s is AudioSource => s !== null);
  return sources;
}

/**
 * Report a station "click" to the Radio Browser API so that statistics are
 * maintained for the community.
 */
export async function reportStationClick(uuid: string): Promise<void> {
  try {
    await radioBrowserFetch(`/url/${uuid}`);
  } catch {
    // Non-critical — ignore silently
  }
}

// ─── Internet Archive — Open Library / radio recordings ──────────────────────

interface ArchiveDoc {
  identifier: string;
  title?: string | string[];
  date?: string;
  creator?: string | string[];
  subject?: string | string[];
  description?: string | string[];
}

interface ArchiveSearchResponse {
  response: { docs: ArchiveDoc[] };
}

// Best-effort country → coordinates lookup
const COUNTRY_COORDS: Array<[string, number, number]> = [
  ['united states', 38.9, -77.0], ['usa', 38.9, -77.0], ['america', 38.9, -77.0],
  ['united kingdom', 51.5, -0.1], ['britain', 51.5, -0.1], ['england', 51.5, -0.1],
  ['germany', 52.5, 13.4], ['france', 48.9, 2.3],      ['italy', 41.9, 12.5],
  ['russia', 55.75, 37.6],       ['china', 39.9, 116.4], ['japan', 35.7, 139.7],
  ['india', 28.6, 77.2],         ['brazil', -15.8, -47.9], ['australia', -35.3, 149.1],
  ['canada', 45.4, -75.7],       ['mexico', 19.4, -99.1], ['spain', 40.4, -3.7],
  ['netherlands', 52.4, 4.9],    ['switzerland', 46.9, 7.5],
  ['nigeria', 9.1, 7.4],         ['south africa', -25.7, 28.2],
  ['egypt', 30.1, 31.2],         ['turkey', 39.9, 32.9],
  ['argentina', -34.6, -58.4],   ['colombia', 4.7, -74.1],
];

function archiveItemToSource(doc: ArchiveDoc): AudioSource | null {
  if (!doc.identifier) return null;
  const title = Array.isArray(doc.title) ? doc.title[0] : (doc.title ?? doc.identifier);
  const text = [
    Array.isArray(doc.subject)     ? doc.subject.join(' ')     : doc.subject,
    Array.isArray(doc.creator)     ? doc.creator.join(' ')     : doc.creator,
    Array.isArray(doc.description) ? doc.description.join(' ') : doc.description,
    title,
  ].filter(Boolean).join(' ').toLowerCase();

  let lat = 0;
  let lng = 0;
  for (const [country, clat, clng] of COUNTRY_COORDS) {
    if (text.includes(country)) { lat = clat; lng = clng; break; }
  }
  // Spread unknowns across globe using identifier hash
  if (lat === 0 && lng === 0) {
    let h = 0;
    for (const c of doc.identifier) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff;
    lat = ((h % 140) - 70);
    lng = (((h >> 7) % 340) - 170);
  }

  const desc = Array.isArray(doc.description) ? doc.description[0] : doc.description;
  const subj = Array.isArray(doc.subject) ? doc.subject.slice(0, 3).join(', ') : (doc.subject ?? '');

  return {
    id: `archive-${doc.identifier}`,
    type: 'archive',
    title: title.trim() || doc.identifier,
    country: '',
    countrycode: '',
    language: '',
    lat,
    lng,
    streamUrl: `https://archive.org/download/${doc.identifier}`,
    favicon: `https://archive.org/services/img/${doc.identifier}`,
    tags: subj ? subj.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 4) : [],
    date: doc.date ? new Date(doc.date) : undefined,
    description: desc?.replace(/<[^>]*>/g, '').slice(0, 200),
  };
}

/**
 * Fetch historical audio recordings from Internet Archive near a given date.
 * Results are geo-estimated from their subject/creator metadata.
 */
export async function fetchArchiveAudio(date: Date, limit = 40): Promise<AudioSource[]> {
  const year = date.getFullYear();
  const q = encodeURIComponent(
    `mediatype:audio (subject:radio OR subject:"radio broadcast" OR collection:radioprograms) AND year:${year}`,
  );
  const fl = 'identifier,title,date,creator,subject,description';
  const url =
    `https://archive.org/advancedsearch.php?q=${q}` +
    `&fl=${encodeURIComponent(fl)}&sort=${encodeURIComponent('date desc')}` +
    `&rows=${limit}&output=json`;

  try {
    const res = await fetch(url, { headers: { 'User-Agent': 'VoxAether/1.0' } });
    if (!res.ok) return [];
    const data = (await res.json()) as ArchiveSearchResponse;
    return (data.response?.docs ?? [])
      .map(archiveItemToSource)
      .filter((s): s is AudioSource => s !== null);
  } catch {
    return [];
  }
}

// ─── Podcast Index — via worker proxy ─────────────────────────────────────────

interface PodcastProxyItem {
  id: string;
  title: string;
  streamUrl: string;
  image: string;
  language: string;
  lat: number;
  lng: number;
  description?: string;
}

interface PodcastProxyResponse {
  podcasts?: PodcastProxyItem[];
  error?: string;
}

export async function fetchPodcastsNear(lat: number, lng: number): Promise<AudioSource[]> {
  try {
    const url = `${WORKER_BASE_URL}/api/podcasts/nearby?lat=${lat}&lon=${lng}&dist=600`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = (await res.json()) as PodcastProxyResponse;
    if (!data.podcasts) return [];

    return data.podcasts
      .filter((p) => p.lat !== 0 || p.lng !== 0)
      .map((p) => ({
        id: p.id,
        type: 'podcast' as AudioSourceType,
        title: p.title,
        country: '',
        countrycode: '',
        language: p.language ?? '',
        lat: p.lat,
        lng: p.lng,
        streamUrl: p.streamUrl,
        favicon: p.image,
        tags: ['podcast'],
        description: p.description,
      }));
  } catch {
    return [];
  }
}

// ─── Global Voices — via worker proxy ─────────────────────────────────────────

interface GVProxyItem {
  id: string;
  title: string;
  region: string;
  lat: number;
  lng: number;
  streamUrl: string;
  description?: string;
  date?: string;
}

interface GVProxyResponse {
  items?: GVProxyItem[];
  error?: string;
}

export async function fetchGlobalVoice(): Promise<AudioSource[]> {
  try {
    const res = await fetch(`${WORKER_BASE_URL}/api/globalvoice`);
    if (!res.ok) return [];
    const data = (await res.json()) as GVProxyResponse;
    if (!data.items) return [];

    return data.items.map((item) => ({
      id: item.id,
      type: 'editorial' as AudioSourceType,
      title: item.title,
      country: item.region,
      countrycode: '',
      language: 'en',
      lat: item.lat,
      lng: item.lng,
      streamUrl: item.streamUrl,
      favicon: 'https://globalvoices.org/favicon.ico',
      tags: ['global voices', item.region.toLowerCase()],
      date: item.date ? new Date(item.date) : undefined,
      description: item.description,
    }));
  } catch {
    return [];
  }
}

// ─── SomaFM — curated editorial internet radio ────────────────────────────────
// Streams are CORS-enabled. Geo-pins reflect cultural/thematic identity.

export function getEditorialStations(): AudioSource[] {
  return [
    { id: 'soma-groovesalad',  type: 'editorial', title: 'Groove Salad',      lat: 52.37, lng: 4.89,    country: 'Netherlands', countrycode: 'NL', language: 'en', streamUrl: 'https://ice1.somafm.com/groovesalad-128-mp3',   favicon: 'https://somafm.com/img3/groovesalad-400.jpg',   tags: ['ambient', 'electronic', 'chill'] },
    { id: 'soma-suburbsofgoa', type: 'editorial', title: 'Suburbs of Goa',    lat: 15.30, lng: 74.08,   country: 'India',       countrycode: 'IN', language: 'en', streamUrl: 'https://ice1.somafm.com/suburbsofgoa-128-mp3', favicon: 'https://somafm.com/img3/suburbsofgoa-400.jpg',  tags: ['goa trance', 'psychedelic', 'world'] },
    { id: 'soma-dronezone',    type: 'editorial', title: 'Drone Zone',         lat: 37.77, lng: -122.42, country: 'United States', countrycode: 'US', language: 'en', streamUrl: 'https://ice1.somafm.com/dronezone-128-mp3',    favicon: 'https://somafm.com/img3/dronezone-400.jpg',     tags: ['drone', 'ambient', 'space'] },
    { id: 'soma-thistle',      type: 'editorial', title: 'Thistle Radio',      lat: 55.86, lng: -4.25,  country: 'Scotland',    countrycode: 'GB', language: 'en', streamUrl: 'https://ice1.somafm.com/thistle-128-mp3',      favicon: 'https://somafm.com/img3/thistle-400.jpg',       tags: ['celtic', 'folk', 'scotland'] },
    { id: 'soma-bootliquor',   type: 'editorial', title: 'Boot Liquor',        lat: 36.17, lng: -86.78, country: 'United States', countrycode: 'US', language: 'en', streamUrl: 'https://ice1.somafm.com/bootliquor-128-mp3',   favicon: 'https://somafm.com/img3/bootliquor-400.jpg',    tags: ['country', 'americana', 'roots'] },
    { id: 'soma-u80s',         type: 'editorial', title: 'Underground 80s',    lat: 42.33, lng: -83.05, country: 'United States', countrycode: 'US', language: 'en', streamUrl: 'https://ice1.somafm.com/u80s-128-mp3',         favicon: 'https://somafm.com/img3/u80s-400.jpg',          tags: ['80s', 'synth', 'new wave'] },
    { id: 'soma-bagelradio',   type: 'editorial', title: 'Bagel Radio',        lat: 40.71, lng: -74.01, country: 'United States', countrycode: 'US', language: 'en', streamUrl: 'https://ice1.somafm.com/bagel-128-mp3',        favicon: 'https://somafm.com/img3/bagel-400.jpg',         tags: ['eclectic', 'alternative', 'indie'] },
    { id: 'soma-defcon',       type: 'editorial', title: 'DEF CON Radio',      lat: 36.17, lng: -115.14, country: 'United States', countrycode: 'US', language: 'en', streamUrl: 'https://ice1.somafm.com/defcon-128-mp3',      favicon: 'https://somafm.com/img3/defcon-400.jpg',        tags: ['electronic', 'hacker', 'dark'] },
    { id: 'soma-deepspaceone', type: 'editorial', title: 'Deep Space One',     lat: -23.5, lng: 133.5,  country: 'Australia',   countrycode: 'AU', language: 'en', streamUrl: 'https://ice1.somafm.com/deepspaceone-128-mp3', favicon: 'https://somafm.com/img3/deepspaceone-400.jpg',  tags: ['space', 'ambient', 'experimental'] },
    { id: 'soma-reggae',       type: 'editorial', title: 'SomaFM Reggae',      lat: 17.99, lng: -76.79, country: 'Jamaica',     countrycode: 'JM', language: 'en', streamUrl: 'https://ice1.somafm.com/reggae-128-mp3',       favicon: 'https://somafm.com/img3/reggae-400.jpg',        tags: ['reggae', 'dub', 'jamaica'] },
    { id: 'soma-secretagent',  type: 'editorial', title: 'Secret Agent',       lat: 48.85, lng: 2.35,   country: 'France',      countrycode: 'FR', language: 'en', streamUrl: 'https://ice1.somafm.com/secretagent-128-mp3',  favicon: 'https://somafm.com/img3/secretagent-400.jpg',   tags: ['lounge', 'spy jazz', 'cinematic'] },
    { id: 'soma-dubstep',      type: 'editorial', title: 'Dubstep Beyond',     lat: 51.51, lng: -0.12,  country: 'United Kingdom', countrycode: 'GB', language: 'en', streamUrl: 'https://ice1.somafm.com/dubstep-128-mp3',   favicon: 'https://somafm.com/img3/dubstep-400.jpg',       tags: ['dubstep', 'bass', 'london'] },
    { id: 'soma-indiepop',     type: 'editorial', title: 'Indie Pop Rocks',    lat: 53.48, lng: -2.24,  country: 'United Kingdom', countrycode: 'GB', language: 'en', streamUrl: 'https://ice1.somafm.com/indiepop-128-mp3',  favicon: 'https://somafm.com/img3/indiepop-400.jpg',      tags: ['indie', 'pop', 'alternative'] },
    { id: 'soma-lush',         type: 'editorial', title: 'Lush',               lat: 35.68, lng: 139.69, country: 'Japan',       countrycode: 'JP', language: 'en', streamUrl: 'https://ice1.somafm.com/lush-128-mp3',         favicon: 'https://somafm.com/img3/lush-400.jpg',          tags: ['chillout', 'downtempo', 'female vocals'] },
    { id: 'soma-metal',        type: 'editorial', title: 'Metal Detector',     lat: 59.33, lng: 18.07,  country: 'Sweden',      countrycode: 'SE', language: 'en', streamUrl: 'https://ice1.somafm.com/metal-128-mp3',        favicon: 'https://somafm.com/img3/metal-400.jpg',         tags: ['metal', 'heavy', 'rock'] },
  ];
}

// Falls back to the production API; override with VITE_WORKER_URL for local dev.
export const WORKER_BASE_URL: string =
  (import.meta.env.VITE_WORKER_URL as string | undefined) ?? 'https://api.voxaether.com';

export async function transcribeAudio(req: TranscribeRequest): Promise<TranscribeResponse> {
  const res = await fetch(`${WORKER_BASE_URL}/api/transcribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    return { text: '', error: err.error ?? `Worker error ${res.status}` };
  }
  return res.json() as Promise<TranscribeResponse>;
}

// ─── Country → flag emoji helper ─────────────────────────────────────────────

/** Convert a 2-letter ISO country code to its flag emoji */
export function countryCodeToFlag(code: string): string {
  if (!code || code.length !== 2) return '🌐';
  const offset = 127397; // 0x1F1E6 - 'A'.charCodeAt(0)
  return String.fromCodePoint(
    code.toUpperCase().charCodeAt(0) + offset,
    code.toUpperCase().charCodeAt(1) + offset,
  );
}

/** Format a bitrate number as a readable string */
export function formatBitrate(kbps: number | undefined): string {
  if (!kbps) return '';
  return `${kbps} kbps`;
}
